import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { PhoneHelperService } from '@helpers/phone-helper.service';
import { PabxCallDataI, ReportCdrCallAudioDataI, ReportCdrFindAllI } from '@interfaces/pabx-call-data.interface';
import { IQueuePabx } from '@interfaces/pabx/queue.interface';
import { ITrunkPabx } from '@interfaces/pabx/trunk.interface';
import { RegionPhoneDataI } from '@interfaces/region-phone-data.interface';
import { firstValueFrom, Observable } from 'rxjs';
import { ReportCallsUraDetailItemI } from '@interfaces/report-calls-ura.interface';

@Injectable({
  providedIn: 'root',
})
export class PabxService {
  private baseUrl = environment.API_PABX;
  private regionUrl = this.baseUrl + 'pabx/routes/routes';

  constructor(private http: HttpClient, private readonly phoneHelperService: PhoneHelperService) {}

  public getTrunks(customerId: string): Observable<any> {
    return this.http.post<any[]>(
      `${this.baseUrl}pabx/trunks/findAll/${customerId}`,
      {},
      {
        params: {
          page: 1,
          limit: 999,
        },
      }
    );
  }

  public getTrunksWithAi(customerId: string): Observable<any> {
    return this.http.post<any[]>(
      `${this.baseUrl}pabx/trunks/findAllWithAi/${customerId}`,
      {},
      {
        params: {
          page: 1,
          take: 999,
        },
      }
    );
  }

  public getTrunksWithoutAi(customerId: string): Observable<any> {
    return this.http.post<any[]>(
      `${this.baseUrl}pabx/trunks/findAllWithOutAi/${customerId}`,
      {},
      {
        params: {
          page: 1,
          take: 999,
        },
      }
    );
  }

  // Lists the customer's DIDs (caller_id candidates) from the PABX
  public getDids(customerId: string): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}pabx/did/findAll/${customerId}`,
      {},
      {
        params: {
          page: 1,
          limit: 999,
        },
      }
    );
  }

  public async getAllAnnouncements(
    customerId: string
  ): Promise<{ data: { _id: string; channel: { name: string } }[] }> {
    return firstValueFrom(this.http.post<any>(`${this.baseUrl}pabx/announcement/findAll/${customerId}`, {}));
  }

  public async getURAS(customerId: string): Promise<{ data: { _id: string; channel: { name: string } }[] }> {
    return firstValueFrom(this.http.get<any>(`${this.baseUrl}pabx/ivr-omni/findAll/${customerId}`));
  }

  public async getFindAllIVRS(customerId: string): Promise<{ data: { _id: string; channel: { name: string } }[] }> {
    return firstValueFrom(
      this.http.post<any>(
        `${this.baseUrl}pabx/ivr/findAll/${customerId}`,
        {},
        {
          params: {
            page: 1,
            limit: 999,
          },
        }
      )
    );
  }

  public async getAllTimeConditions(
    customerId: string
  ): Promise<{ data: { _id: string; channel: { name: string } }[] }> {
    return firstValueFrom(
      this.http.post<any>(
        `${this.baseUrl}pabx/time-group/findAll/${customerId}`,
        {},
        {
          params: {
            page: 1,
            limit: 999,
          },
        }
      )
    );
  }

  public async getAllCustomIvr(customerId: string): Promise<{ data: { _id: string; channel: { name: string } }[] }> {
    return firstValueFrom(
      this.http.post<any>(
        `${this.baseUrl}pabx/ivr-custom/findAll/${customerId}`,
        {},
        {
          params: {
            page: 1,
            limit: 999,
          },
        }
      )
    );
  }

  public async getAllSoftphones(customerId: string): Promise<{ data: { _id: string; channel: { name: string } }[] }> {
    return firstValueFrom(
      this.http.post<any>(
        `${this.baseUrl}pabx/ramals/findAll/${customerId}`,
        {},
        {
          params: {
            page: 1,
            limit: 999,
          },
        }
      )
    );
  }

  public getAllQueues(
    customerId: string,
    onlyCallCenter = true
  ): Promise<{ data: { _id: string; channel: { name: string } }[] }> {
    return firstValueFrom(
      this.http.post<any>(
        `${this.baseUrl}pabx/queue-ast/findAll/${customerId}${onlyCallCenter ? '/omni' : ''}`,
        {},
        {
          params: {
            page: 1,
            limit: 999,
          },
        }
      )
    );
  }
  public getAllCallCenters(customerId: string): Promise<{ data: { _id: string; channel: { name: string } }[] }> {
    return this.getAllQueues(customerId);
  }

  public async getReportIvrDetail(customerId: string, uniqueid: string): Promise<ReportCallsUraDetailItemI[]> {
    return firstValueFrom(
      this.http.get<any>(`${this.baseUrl}pabx/ivr-omni/report/findHistory/${customerId}/details/${uniqueid}`)
    );
  }

  public getMusics(customerId: string): Observable<any> {
    return this.http.post<any[]>(
      `${this.baseUrl}pabx/audio/findAll/${customerId}`,
      {},
      {
        params: {
          page: 1,
          limit: 999,
        },
      }
    );
  }

  public getCallbacks(customerId: string): Observable<any> {
    return this.http.get<any[]>(`${this.baseUrl}pabx/callback/config/customer/${customerId}`, {
      params: {
        page: 1,
        limit: 999,
      },
    });
  }

  public getSurveys(customerId: string): Observable<any> {
    return this.http.post<any[]>(
      `${this.baseUrl}pabx/survey/findAll/${customerId}`,
      {},
      {
        params: {
          page: 1,
          limit: 999,
        },
      }
    );
  }

  public updateStatusRamal(ramal: string, status: string): Observable<any> {
    return this.http.post<any[]>(`${this.baseUrl}pabx/queue-members/update/${ramal}/${status}`, {});
  }

  public verifyRamal(id: string, password: string, server: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${this.baseUrl}pabx/ramals/verifyRamal/${id}/${password}/${server}`, {}, {})
    );
  }

  public getRamalData(ramal: string) {
    const ramalUrl = `${this.baseUrl}pabx/ramals/findByAsterisk/${ramal}`;
    return this.http.post<any>(ramalUrl, {});
  }

  public getQueueById(idQueue: string): Observable<IQueuePabx> {
    return this.http.post<IQueuePabx>(`${this.baseUrl}pabx/queue-ast/findBy/${idQueue}`, {});
  }

  public geTrunkById(idTrunk: string): Observable<ITrunkPabx> {
    return this.http.post<ITrunkPabx>(`${this.baseUrl}pabx/trunks/findBy/${idTrunk}`, {});
  }

  public updateByUniqueId(uniqueid: string, additionalInfoObject: any) {
    const ramalUrl = `${this.baseUrl}pabx/report-cdr/omni/${uniqueid}`;
    for (const key in additionalInfoObject) {
      if (!additionalInfoObject[key]) {
        delete additionalInfoObject[key];
      }
    }
    return this.http.put<any>(ramalUrl, additionalInfoObject);
  }

  public async getReportCdrCallData(
    externalCallId: string,
    customer: string,
    identifier: { destinyId?: string; sourceId?: string }
  ): Promise<PabxCallDataI | null> {
    if (!externalCallId) {
      return null;
    }
    const params = {
      sort: 'desc',
      active: 'createdAt',
      limit: 1,
      page: 1,
      ...(identifier.sourceId && { sourceId: identifier.sourceId }),
      ...(identifier.destinyId && { destinyId: identifier.destinyId }),
    };

    const cdrUrl = `${this.baseUrl}pabx/report-cdr/omni/findAll/${customer}`;

    const resultCdr = await firstValueFrom(
      this.http.post<{ data: ReportCdrFindAllI[] }>(
        cdrUrl,
        {
          $or: [{ 'data.uniqueidExtra': externalCallId }, { 'data.uniqueid': externalCallId }],
        },
        { params }
      )
    );
    const row = resultCdr?.data?.[0];
    const rowData = row?.data ?? {};

    if (!rowData || !Object?.keys(rowData)?.length) {
      return null;
    }
    const audioData = (rowData?.audio as any)?.data as ReportCdrCallAudioDataI;
    const resultData: PabxCallDataI = {
      id: rowData?.uniqueid,
      date: rowData?.date,
      source: rowData?.source ?? '',
      destiny: rowData?.destiny ?? '',
      sourceId: rowData?.sourceId ?? '',
      destinyId: rowData?.destinyId ?? '',
      channel: (rowData?.trunkId as any)?.channel,
      trunkId: rowData?.trunkId as any,
      from: rowData?.from,
      duration: rowData?.duration || rowData?.billsec || '0',
      pathAudio: audioData?.path ? `${audioData?.path}${audioData?.filename}.${audioData?.extensions}` : undefined,
      extension: audioData?.extensions,
      uniqueid: rowData?.uniqueid,
      type: rowData?.type,
      status: rowData?.status,
      hangup: rowData?.hangup,
      address: row?.address,
      start: rowData?.start,
      end: rowData?.end,
      apiUrl: this.baseUrl,
      ramal: (rowData?.type === 'OUTLINE' ? rowData?.source : rowData?.destiny) as any,
      billsec: rowData?.billsec,
    };

    for (const key in resultData) {
      if (!resultData[key]) {
        delete resultData[key];
      }
    }
    return resultData;
  }

  public getNumberRegion(phone: string): Observable<any> | null {
    const data = this.getRegionDataFromPhone(phone);

    return data ? this.http.post<IQueuePabx>(this.regionUrl, data) : null;
  }

  // Activates the PABX Routes feature (ft_dialplan) for the customer
  public enablePabxRoutes(customerId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}pabx/routes/omni`, { customerId });
  }

  // Deactivates the PABX Routes feature (ft_dialplan) for the customer
  public disablePabxRoutes(customerId: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}pabx/routes/omni/${customerId}`);
  }

  private getRegionDataFromPhone(phone: string): RegionPhoneDataI | null {
    const formattedPhone = this.phoneHelperService.formatPhone(phone);

    if (!formattedPhone) {
      return null;
    }

    // Extrai o DDD (dois digitos depois do 55)
    const ddd = formattedPhone.substring(2, 4);
    let prefix = '';

    if (this.phoneHelperService.checkIfIsMobilePhone(formattedPhone)) {
      // Prefixo movel = 5 primeiros digitos depois do 55 e DDD
      prefix = formattedPhone.substring(4, 9);
    } else {
      // Prefixo fixo = 4 primeiros digitos depois do 55 e DDD
      prefix = formattedPhone.substring(4, 8);
    }

    return { ddd: ddd, prefixo: prefix };
  }
}
